import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../services/localeHelpers';
import { ScanLine, CheckCircle2, Clock, AlertCircle, Loader2, MapPin, Filter, Map, Play, Square, RotateCcw, ShieldCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../services/firebaseService';
import { ALL_PRESCAN_REGIONS, NORDIC_PRESCAN_GROUPS, generateGrid } from '../../../utils/prescanRegions';

interface RegionPrescanStatus {
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  cellsTotal: number;
  cellsCompleted: number;
  cellsFailed: number;
  spotsFound: number;
  spotsKept: number;
  startedAt: string | null;
  completedAt: string | null;
}

interface PrescanStatusData {
  lastUpdated: string;
  regions: Record<string, RegionPrescanStatus>;
}

const ALL_REGIONS = ALL_PRESCAN_REGIONS.map(region => region.name);

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  completed:   { bg: 'bg-emerald-500/10 border-emerald-500/20', text: 'text-emerald-500', label: 'admin.prescan.status_done' },
  in_progress: { bg: 'bg-amber-500/10 border-amber-500/20',    text: 'text-amber-500',   label: 'admin.prescan.status_running' },
  pending:     { bg: 'bg-[var(--bg-input)] border-[var(--border-secondary)]', text: 'text-[var(--text-muted)]', label: 'admin.prescan.status_waiting' },
  failed:      { bg: 'bg-red-500/10 border-red-500/20',        text: 'text-red-500',      label: 'admin.prescan.status_failed' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(n: number): string {
  return n.toLocaleString('nb-NO');
}

const SCAN_DELAY_MS = 5000; // pause mellom celler
const STATUS_UPDATE_INTERVAL = 3; // oppdater Firestore hver N celler
const MIN_PER_CELL = 2.25; // estimert tid per celle i minutter

interface ReverifyStatus {
  status: 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  checked?: number;
  invalidated?: number;
  alreadyInvalid?: number;
  skippedConfirmed?: number;
  barrierFlagged?: number;
  barrierCleared?: number;
  // Fra barrier-revalidation-lib.mjs (lokal kjøring)
  newFlagged?: number;
  cleared?: number;
  cellsTotal?: number;
  cellsCompleted?: number;
  source?: string;
}

const AdminPrescanDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<PrescanStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanningRegion, setScanningRegion] = useState<string | null>(null);
  const [currentCellIndex, setCurrentCellIndex] = useState(0);
  const cancelledRef = useRef(false);
  const [reverifyData, setReverifyData] = useState<Record<string, ReverifyStatus>>({});
  const [reverifyingRegion, setReverifyingRegion] = useState<string | null>(null);
  const [openCountries, setOpenCountries] = useState<Record<string, boolean>>({ Norge: true });

  const statusDocRef = doc(db, 'system', 'prescan_status');
  const countryGroups = useMemo(() => NORDIC_PRESCAN_GROUPS, []);

  const startRegionScan = useCallback(async (regionName: string, resume: boolean) => {
    const region = ALL_PRESCAN_REGIONS.find(r => r.name === regionName);
    if (!region) return;

    cancelledRef.current = false;
    setScanningRegion(regionName);

    const grid = generateGrid(region);
    const startIndex = resume ? (data?.regions?.[regionName]?.cellsCompleted ?? 0) : 0;

    // Initialiser Firestore-status
    if (!resume) {
      await updateDoc(statusDocRef, {
        [`regions.${regionName}`]: {
          status: 'in_progress',
          cellsTotal: grid.length,
          cellsCompleted: 0,
          cellsFailed: 0,
          spotsFound: 0,
          spotsKept: 0,
          startedAt: new Date().toISOString(),
          completedAt: null,
        },
        lastUpdated: new Date().toISOString(),
      });
    } else {
      await updateDoc(statusDocRef, {
        [`regions.${regionName}.status`]: 'in_progress',
        [`regions.${regionName}.completedAt`]: null,
        lastUpdated: new Date().toISOString(),
      });
    }

    const scanFn = httpsCallable(functions, 'scanAreaBatch', { timeout: 1860000 });
    let cellsCompleted = startIndex;
    let cellsFailed = resume ? (data?.regions?.[regionName]?.cellsFailed ?? 0) : 0;
    let spotsFound = resume ? (data?.regions?.[regionName]?.spotsFound ?? 0) : 0;
    let spotsKept = resume ? (data?.regions?.[regionName]?.spotsKept ?? 0) : 0;
    const startedAt = resume
      ? (data?.regions?.[regionName]?.startedAt ?? new Date().toISOString())
      : new Date().toISOString();

    for (let i = startIndex; i < grid.length; i++) {
      if (cancelledRef.current) {
        await updateDoc(statusDocRef, {
          [`regions.${regionName}.status`]: 'pending',
          lastUpdated: new Date().toISOString(),
        });
        break;
      }

      setCurrentCellIndex(i);
      const cell = grid[i];

      let success = false;
      for (let attempt = 0; attempt < 2 && !success; attempt++) {
        try {
          const result = await scanFn({ bounds: cell });
          const rd = result.data as any;
          const stats = rd?.stats ?? {};
          spotsFound += (stats.total ?? 0);
          spotsKept += (stats.kept ?? 0);
          cellsCompleted++;
          success = true;
        } catch (err: any) {
          if (attempt === 1) {
            cellsFailed++;
            console.error(`Celle ${i} feilet:`, err.message);
          }
        }
      }

      // Oppdater Firestore periodisk
      if ((i - startIndex) % STATUS_UPDATE_INTERVAL === 0 || i === grid.length - 1) {
        await updateDoc(statusDocRef, {
          [`regions.${regionName}`]: {
            status: 'in_progress',
            cellsTotal: grid.length,
            cellsCompleted,
            cellsFailed,
            spotsFound,
            spotsKept,
            startedAt,
            completedAt: null,
          },
          lastUpdated: new Date().toISOString(),
        }).catch(() => {});
      }

      // Pause mellom celler
      if (i < grid.length - 1 && !cancelledRef.current) {
        await new Promise(r => setTimeout(r, SCAN_DELAY_MS));
      }
    }

    // Endelig status
    if (!cancelledRef.current) {
      await updateDoc(statusDocRef, {
        [`regions.${regionName}`]: {
          status: cellsFailed > 0 ? 'failed' : 'completed',
          cellsTotal: grid.length,
          cellsCompleted,
          cellsFailed,
          spotsFound,
          spotsKept,
          startedAt,
          completedAt: new Date().toISOString(),
        },
        lastUpdated: new Date().toISOString(),
      });
    }

    setScanningRegion(null);
    setCurrentCellIndex(0);
  }, [data]);

  const stopScan = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const startReverify = useCallback(async (regionName: string) => {
    const region = ALL_PRESCAN_REGIONS.find(r => r.name === regionName);
    if (!region) return;
    setReverifyingRegion(regionName);
    try {
      const reverifyFn = httpsCallable(functions, 'reverifyRegion', { timeout: 1860000 });
      // Fire-and-forget: call without awaiting so admin can close browser
      reverifyFn({
        regionName,
        bounds: { latMin: region.latMin, latMax: region.latMax, lonMin: region.lonMin, lonMax: region.lonMax },
      }).catch(err => console.error('[REVERIFY] Error:', err));
    } catch (err) {
      console.error('[REVERIFY] Failed to start:', err);
    }
    // Reset local state after a short delay — function runs server-side
    setTimeout(() => setReverifyingRegion(null), 3000);
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'system', 'reverify_status'), (snap) => {
      if (snap.exists()) {
        setReverifyData(snap.data() as Record<string, ReverifyStatus>);
      }
    }, () => {});
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'system', 'prescan_status'), (snap) => {
      if (snap.exists()) {
        setData(snap.data() as PrescanStatusData);
      }
      setLoading(false);
    }, (err) => {
      console.error('Prescan status listener error:', err);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
        <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster prescan-status...</p>
      </div>
    );
  }

  if (!data || !data.regions) {
    return (
      <div className="p-6 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl flex items-start gap-4">
        <AlertCircle className="text-[var(--text-muted)] shrink-0" size={18} />
        <div>
          <p className="text-sm font-bold text-[var(--text-primary)]">{t('admin.prescan_no_data', 'Ingen prescan-data')}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Kjør seed-scriptet eller start en prescan for å se status her.</p>
        </div>
      </div>
    );
  }

  const regions = data.regions;
  const completedCount = ALL_REGIONS.filter(r => regions[r]?.status === 'completed').length;
  const inProgressCount = ALL_REGIONS.filter(r => regions[r]?.status === 'in_progress').length;
  const totalFound = ALL_REGIONS.reduce((s, r) => s + (regions[r]?.spotsFound ?? 0), 0);
  const totalKept = ALL_REGIONS.reduce((s, r) => s + (regions[r]?.spotsKept ?? 0), 0);
  const filterRate = totalFound > 0 ? ((totalFound - totalKept) / totalFound * 100).toFixed(1) : '0';

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <Map size={16} className="text-cyan-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.prescan_total_regions', 'Fylker skannet')}</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{completedCount} <span className="text-lg text-[var(--text-muted)]">/ {ALL_REGIONS.length}</span></p>
          {inProgressCount > 0 && <p className="text-2xs text-amber-400 mt-1">{inProgressCount} pågår</p>}
          <div className="w-full bg-[var(--bg-input)] h-1.5 rounded-full mt-3 overflow-hidden">
            <div className="bg-cyan-500 h-full rounded-full transition-all duration-1000" style={{ width: `${(completedCount / ALL_REGIONS.length) * 100}%` }} />
          </div>
        </div>

        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <MapPin size={16} className="text-emerald-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.prescan_total_found', 'Totalt funnet')}</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{formatNumber(totalFound)}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Kandidater fra alle kilder</p>
        </div>

        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <CheckCircle2 size={16} className="text-violet-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.prescan_total_kept', 'Totalt beholdt')}</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{formatNumber(totalKept)}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Spots i cached_spots</p>
        </div>

        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Filter size={16} className="text-orange-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.prescan_filter_rate', 'Filtreringsrate')}</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{filterRate}%</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Fjernet av kvalitetsfiltre</p>
        </div>
      </div>

      {/* Country Groups */}
      {countryGroups.map(group => {
        const isOpen = openCountries[group.country] !== false;
        const countryRegions = group.regions.map(region => region.name);
        const countryCompleted = countryRegions.filter(r => regions[r]?.status === 'completed').length;
        const countryInProgress = countryRegions.filter(r => regions[r]?.status === 'in_progress').length;
        const countryFound = countryRegions.reduce((sum, r) => sum + (regions[r]?.spotsFound ?? 0), 0);
        const countryKept = countryRegions.reduce((sum, r) => sum + (regions[r]?.spotsKept ?? 0), 0);

        return (
          <div key={group.country} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel overflow-hidden">
            <button
              onClick={() => setOpenCountries(prev => ({ ...prev, [group.country]: prev[group.country] === false }))}
              className="w-full flex items-center justify-between gap-4 p-6 md:p-8 text-left hover:bg-[var(--bg-surface)] transition-colors"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 flex items-center justify-center shrink-0">
                  {isOpen ? <ChevronDown size={20} className="text-cyan-400" /> : <ChevronRight size={20} className="text-cyan-400" />}
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-black text-[var(--text-primary)] uppercase tracking-widest">{group.country}</h3>
                  <p className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-widest mt-1">
                    {countryCompleted}/{countryRegions.length} fylker ferdig
                    {countryInProgress > 0 ? ` · ${countryInProgress} pågår` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6 text-right shrink-0">
                <div>
                  <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Funnet</p>
                  <p className="text-sm font-black text-[var(--text-primary)]">{formatNumber(countryFound)}</p>
                </div>
                <div>
                  <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Beholdt</p>
                  <p className="text-sm font-black text-emerald-400">{formatNumber(countryKept)}</p>
                </div>
              </div>
            </button>

            {isOpen && (
              <div className="px-6 md:px-8 pb-6 md:pb-8 space-y-3">
                {countryRegions.map(regionName => {
              const r = regions[regionName];
              const status = r?.status ?? 'pending';
              const style = STATUS_STYLES[status] ?? STATUS_STYLES.pending;
              const cellsTotal = r?.cellsTotal ?? 0;
              const cellsCompleted = r?.cellsCompleted ?? 0;
              const progress = cellsTotal > 0 ? (cellsCompleted / cellsTotal * 100) : 0;
              const regionFilterRate = (r?.spotsFound ?? 0) > 0
                ? (((r.spotsFound - r.spotsKept) / r.spotsFound) * 100).toFixed(0)
                : '–';

              return (
                <div key={regionName} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 p-4 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-primary)]">
                  {/* Name + status */}
                  <div className="flex items-center gap-3 sm:w-[200px] shrink-0">
                    <div className={`w-2.5 h-2.5 rounded-full ${status === 'completed' ? 'bg-emerald-500' : status === 'in_progress' ? 'bg-amber-500 animate-pulse' : status === 'failed' ? 'bg-red-500' : 'bg-[var(--text-muted)]/30'}`} />
                    <span className="text-sm font-bold text-[var(--text-primary)]">{regionName}</span>
                    <span className={`text-3xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${style.bg} ${style.text}`}>
                      {t(style.label)}
                    </span>
                  </div>

                  {/* Progress bar (for in_progress) */}
                  <div className="flex-1 sm:mx-4">
                    {status === 'in_progress' ? (
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-[var(--bg-input)] h-2 rounded-full overflow-hidden">
                          <div className="bg-amber-500 h-full rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-2xs font-bold text-amber-400 whitespace-nowrap">{cellsCompleted}/{cellsTotal}</span>
                      </div>
                    ) : status === 'completed' ? (
                      <span className="text-2xs text-[var(--text-muted)]">{cellsCompleted}/{cellsTotal} celler</span>
                    ) : (
                      <span className="text-2xs text-[var(--text-muted)]">{cellsTotal > 0 ? `${cellsTotal} celler` : '–'}</span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 sm:gap-6 text-2xs shrink-0">
                    <div className="text-center">
                      <p className="font-black text-[var(--text-secondary)] uppercase tracking-widest mb-0.5">{t('admin.prescan_found', 'Funnet')}</p>
                      <p className="text-sm font-bold text-[var(--text-primary)]">{formatNumber(r?.spotsFound ?? 0)}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-black text-[var(--text-secondary)] uppercase tracking-widest mb-0.5">{t('admin.prescan_kept', 'Beholdt')}</p>
                      <p className="text-sm font-bold text-emerald-400">{formatNumber(r?.spotsKept ?? 0)}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-black text-[var(--text-secondary)] uppercase tracking-widest mb-0.5">Filtrert</p>
                      <p className="text-sm font-bold text-[var(--text-muted)]">{regionFilterRate}%</p>
                    </div>
                    <div className="text-center min-w-[80px]">
                      <p className="font-black text-[var(--text-secondary)] uppercase tracking-widest mb-0.5">{t('admin.prescan_completed_at', 'Fullført')}</p>
                      <p className="text-2xs font-medium text-[var(--text-muted)]">{formatDate(r?.completedAt ?? null)}</p>
                    </div>
                  </div>

                  {/* Barrier revalidation status */}
                  {(() => {
                    const rv = reverifyData[regionName];
                    if (!rv) return null;
                    const isActive = rv.status === 'in_progress';
                    const flagged = rv.newFlagged ?? rv.barrierFlagged ?? 0;
                    const clrd    = rv.cleared    ?? rv.barrierCleared  ?? 0;
                    const cellsDone  = rv.cellsCompleted ?? 0;
                    const cellsAll   = rv.cellsTotal ?? 0;
                    const pct = cellsAll > 0 ? Math.round((cellsDone / cellsAll) * 100) : 0;
                    return (
                      <div className="flex items-center gap-2 shrink-0 mx-2 min-w-[120px]">
                        {isActive ? (
                          <div className="flex flex-col gap-0.5 w-full">
                            <div className="flex items-center gap-1.5">
                              <Loader2 size={9} className="animate-spin text-violet-400 shrink-0" />
                              <span className="text-3xs font-bold text-violet-400 whitespace-nowrap">
                                Bom-sjekk {cellsAll > 0 ? `${cellsDone}/${cellsAll}` : '…'}
                              </span>
                            </div>
                            {cellsAll > 0 && (
                              <div className="w-full bg-[var(--bg-input)] h-1 rounded-full overflow-hidden">
                                <div className="bg-violet-500 h-full rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-3xs text-[var(--text-muted)]">
                            <ShieldCheck size={9} className={flagged > 0 || clrd > 0 ? 'text-violet-400' : 'text-[var(--text-muted)]'} />
                            <span>
                              {flagged > 0 && <span className="text-red-400">{flagged}↑ </span>}
                              {clrd    > 0 && <span className="text-emerald-400">{clrd}↓ </span>}
                              {flagged === 0 && clrd === 0 && <span>0 bom</span>}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 shrink-0 sm:ml-4">
                    {/* Reverify button */}
                    {(() => {
                      const rv = reverifyData[regionName];
                      const isReverifying = rv?.status === 'in_progress' || reverifyingRegion === regionName;
                      return (
                        <button
                          onClick={() => startReverify(regionName)}
                          disabled={isReverifying || !!scanningRegion}
                          className={`px-3 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-1.5 transition-all ${
                            isReverifying
                              ? 'bg-violet-600/50 text-white/60 cursor-wait'
                              : scanningRegion
                                ? 'bg-[var(--bg-input)] text-[var(--text-muted)] opacity-40 cursor-not-allowed'
                                : 'bg-violet-600 hover:bg-violet-500 text-white'
                          }`}
                          title={rv?.completedAt ? `Sist: ${formatDate(rv.completedAt)} — ${rv.checked ?? 0} sjekket, ${rv.invalidated ?? 0} fjernet` : 'Reverifier region'}
                        >
                          {isReverifying ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />}
                          {isReverifying ? t('admin.prescan.verifying') : 'Reverifier'}
                        </button>
                      );
                    })()}

                    {/* Scan button */}
                    {scanningRegion === regionName ? (
                      <button
                        onClick={stopScan}
                        className="px-3 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white transition-all"
                      >
                        <Square size={10} /> Stopp
                      </button>
                    ) : scanningRegion ? (
                      <button
                        disabled
                        className="px-3 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-1.5 bg-[var(--bg-input)] text-[var(--text-muted)] opacity-40 cursor-not-allowed"
                      >
                        <Play size={10} /> Start
                      </button>
                    ) : (status !== 'completed' && cellsCompleted > 0) ? (
                      <button
                        onClick={() => startRegionScan(regionName, true)}
                        className="px-3 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white transition-all"
                      >
                        <RotateCcw size={10} /> Gjenoppta
                      </button>
                    ) : (
                      <button
                        onClick={() => startRegionScan(regionName, false)}
                        className="px-3 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-500 text-white transition-all"
                      >
                        <Play size={10} /> {status === 'completed' ? 'Skann på nytt' : 'Start'}
                      </button>
                    )}
                  </div>

                  {/* Reverify status line */}
                  {reverifyData[regionName]?.completedAt && (
                    <div className="w-full mt-2 text-3xs text-[var(--text-muted)] pl-6">
                      Sist reverifisert: {formatDate(reverifyData[regionName].completedAt!)} — {reverifyData[regionName].checked ?? 0} sjekket, <span className={`${(reverifyData[regionName].invalidated ?? 0) > 0 ? 'text-red-400 font-bold' : ''}`}>{reverifyData[regionName].invalidated ?? 0} fjernet</span>{(reverifyData[regionName].barrierFlagged ?? 0) > 0 && <>, <span className="text-orange-400 font-bold">{reverifyData[regionName].barrierFlagged} bom-flagget</span></>}{(reverifyData[regionName].barrierCleared ?? 0) > 0 && <>, <span className="text-green-400 font-bold">{reverifyData[regionName].barrierCleared} bom-fjernet</span></>}
                    </div>
                  )}
                  {reverifyData[regionName]?.status === 'in_progress' && (
                    <div className="w-full mt-2 text-3xs text-violet-400 pl-6 flex items-center gap-1.5">
                      <Loader2 size={10} className="animate-spin" /> Reverifiserer... ({reverifyData[regionName].checked ?? 0} sjekket)
                    </div>
                  )}
                </div>
              );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Last updated */}
      {data.lastUpdated && (
        <p className="text-2xs text-[var(--text-muted)] text-center">
          {t('admin.prescan_last_updated', 'Sist oppdatert')}: {formatDate(data.lastUpdated)}
        </p>
      )}
    </div>
  );
};

export default AdminPrescanDashboard;
